import {
  getMarketingBrandFromHostname,
  isEasyGPTMarketingHost as isEasyGPTMarketingHostFromBrand,
  isSiteGlueMarketingHost,
  MARKETING_BRAND_QUERY_KEY,
  MARKETING_BRAND_SITEGLUE,
  parseMarketingBrandParam,
  resolveClientMarketingBrand,
  type MarketingBrandId,
  withMarketingBrandParam,
} from "~/lib/marketing-brand";
import { appendAttributionToParams, appendAttributionToUrl } from "~/lib/siteglue/marketing-attribution";

/** Clerk allowed subdomain used for auth when www is not in the Clerk dashboard. */
export const CLERK_AUTH_APP_ORIGIN = "https://app.easygpt.builders";

/** Relative auth routes — work on easygpt.builders (primary) and app.easygpt.builders. */
export const SIGN_IN_PATH = "/sign-in";
export const SIGN_UP_PATH = "/sign-up";
/** Signed-in users land here from marketing home and public nav. */
export const APP_DASHBOARD_PATH = "/projects";

/** SiteGlue “Sign up” → onboard with Trish on app (not Clerk /sign-up). */
export const SITEGLUE_TRISH_ONBOARD_PATH =
  `${CLERK_AUTH_APP_ORIGIN}/agents/hire/custom?brand=siteglue` as const;

/** Inbound GPT “Sign up” → custom hire builder (not Clerk /sign-up). */
export const INBOUND_GPT_HIRE_CUSTOM_PATH =
  `${CLERK_AUTH_APP_ORIGIN}/agents/hire/custom` as const;

/** Public marketing site (Vercel canonical host — apex redirects here). */
export const EASYGPT_MARKETING_ORIGIN = "https://www.easygpt.builders";

/** SiteGlue AI marketing twin (same Vercel project, hostname brand). */
export const SITEGLUE_MARKETING_ORIGIN = "https://www.siteglue.ai";

/** Clerk satellite domain registered for www.siteglue.ai (Frontend API: clerk.www.siteglue.ai). */
export const SITEGLUE_CLERK_SATELLITE_DOMAIN = "www.siteglue.ai";

/**
 * Query flag that tells SiteGlue middleware to run the Clerk satellite handshake
 * after Google sign-in on the primary app. Absent on ordinary marketing visits.
 */
export const SITEGLUE_CLERK_SYNC_QUERY = "sg_auth";

/** PracticeClaw marketing origin. */
export const PRACTICECLAW_MARKETING_ORIGIN = "https://www.practiceclaw.com";

/**
 * Where to send the browser after Clerk sign-out.
 * Product brand (useBrand) wins over org name — signing in from easygpt.builders
 * must return to www.easygpt.builders even if the active Clerk org mentions SiteGlue.
 */
export function getPostSignOutRedirectUrl(options?: {
  brandId?: string | null;
  organizationName?: string | null;
  organizationSlug?: string | null;
}): string {
  const brandId = (options?.brandId ?? "").toLowerCase();
  const orgBlob =
    `${options?.organizationName ?? ""} ${options?.organizationSlug ?? ""}`.toLowerCase();

  // Explicit product brand from app chrome (useBrand / brands config).
  if (brandId === "siteglue") return SITEGLUE_MARKETING_ORIGIN;
  if (brandId === "practiceclaw") return PRACTICECLAW_MARKETING_ORIGIN;
  if (brandId === "easygpt" || brandId === "inbound_gpt") {
    return EASYGPT_MARKETING_ORIGIN;
  }

  // SiteGlue marketing twin session when brandId was omitted.
  if (typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromQuery = params.get(MARKETING_BRAND_QUERY_KEY)?.trim().toLowerCase();
      const fromSession = sessionStorage.getItem("eg_marketing_brand");
      if (fromQuery === MARKETING_BRAND_SITEGLUE || fromSession === MARKETING_BRAND_SITEGLUE) {
        return SITEGLUE_MARKETING_ORIGIN;
      }
    } catch {
      /* ignore */
    }
  }

  // Org name heuristics only when brand is unknown.
  if (orgBlob.includes("siteglue")) return SITEGLUE_MARKETING_ORIGIN;
  if (orgBlob.includes("practiceclaw")) return PRACTICECLAW_MARKETING_ORIGIN;

  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (isSiteGlueMarketingHost(host)) return SITEGLUE_MARKETING_ORIGIN;
    if (host.includes("practiceclaw.com")) return PRACTICECLAW_MARKETING_ORIGIN;
    // Inbound GPT app + marketing hosts → public EasyGPT home (not app.easygpt.builders).
    if (isEasyGPTAppHost(host) || isEasyGPTMarketingHostFromBrand(host)) {
      return EASYGPT_MARKETING_ORIGIN;
    }
    return `${window.location.origin}/`;
  }

  return EASYGPT_MARKETING_ORIGIN;
}

export function isEasyGPTAppHost(hostname: string): boolean {
  return (
    hostname.includes("app.easygpt.builders") ||
    hostname === "staging.easygpt.builders"
  );
}

export function isEasyGPTMarketingHost(hostname: string): boolean {
  return isEasyGPTMarketingHostFromBrand(hostname);
}

export { isSiteGlueMarketingHost };

export function marketingOriginForBrand(brand: MarketingBrandId = "inbound_gpt"): string {
  return brand === "siteglue" ? SITEGLUE_MARKETING_ORIGIN : EASYGPT_MARKETING_ORIGIN;
}

export function marketingOriginForHostname(hostname: string): string {
  return marketingOriginForBrand(getMarketingBrandFromHostname(hostname));
}

/**
 * Absolute URL on the marketing site.
 * Pass `hostname` (or brand) when generating sitemaps/canonicals for SiteGlue.
 */
export function marketingSiteUrl(
  path: string = "/",
  options?: { hostname?: string; brand?: MarketingBrandId },
): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  let brand = options?.brand;
  if (!brand && options?.hostname) {
    brand = getMarketingBrandFromHostname(options.hostname);
  }
  if (!brand && typeof window !== "undefined") {
    brand = getMarketingBrandFromHostname(window.location.hostname);
  }
  return `${marketingOriginForBrand(brand ?? "inbound_gpt")}${normalized}`;
}

export function brandMarketingSiteUrl(brand: MarketingBrandId, path: string = "/"): string {
  return marketingSiteUrl(path, { brand });
}

export function isClerkAuthPath(pathname: string): boolean {
  return pathname.startsWith(SIGN_IN_PATH) || pathname.startsWith(SIGN_UP_PATH);
}

export function buildSignInHref(params?: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${SIGN_IN_PATH}?${query}` : SIGN_IN_PATH;
}

export function clerkSignInAbsoluteUrl(params?: Record<string, string | undefined>): string {
  return `${CLERK_AUTH_APP_ORIGIN}${buildSignInHref(params)}`;
}

export function clerkSignUpAbsoluteUrl(params?: Record<string, string | undefined>): string {
  return `${CLERK_AUTH_APP_ORIGIN}${buildSignUpHref(params)}`;
}

/**
 * Google OAuth must start in the browser on the primary Clerk domain.
 * Server-side Frontend API sign-ups are rejected (captcha), and a satellite
 * origin cannot call clerk.easygpt.builders directly.
 */
export function buildGoogleOAuthStartUrl(returnTo: string): string {
  const back = new URL(returnTo);
  if (isSiteGlueMarketingHost(back.hostname)) {
    back.searchParams.set(SITEGLUE_CLERK_SYNC_QUERY, "1");
  }
  const url = new URL("/sign-in/google", CLERK_AUTH_APP_ORIGIN);
  url.searchParams.set("redirect_url", back.toString());
  return url.toString();
}

/**
 * Explicit brand query for marketing → app auth links.
 * Prefer an explicit `brand` param, then persisted SiteGlue session, then hostname.
 * EasyGPT marketing hosts still send brand=inbound_gpt so a prior SiteGlue session
 * does not keep showing SiteGlue chrome on /sign-in (pass brand=inbound_gpt explicitly).
 */
function marketingAuthBrandParams(
  params?: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const explicit = parseMarketingBrandParam(params?.[MARKETING_BRAND_QUERY_KEY]);
  let brand: MarketingBrandId = explicit ?? "inbound_gpt";
  if (!explicit && typeof window !== "undefined") {
    brand = resolveClientMarketingBrand(
      new URLSearchParams(window.location.search),
    );
    if (
      brand !== "siteglue" &&
      getMarketingBrandFromHostname(window.location.hostname) === "siteglue"
    ) {
      brand = "siteglue";
    }
  }
  return {
    ...params,
    [MARKETING_BRAND_QUERY_KEY]:
      brand === "siteglue" ? MARKETING_BRAND_SITEGLUE : "inbound_gpt",
  };
}

/**
 * Sign-in URL for the current marketing host (always tags brand for the twin).
 * Safe to call from client components.
 */
export function marketingClerkSignInAbsoluteUrl(
  params?: Record<string, string | undefined>,
): string {
  return clerkSignInAbsoluteUrl(marketingAuthBrandParams(params));
}

export function marketingClerkSignUpAbsoluteUrl(
  params?: Record<string, string | undefined>,
): string {
  return clerkSignUpAbsoluteUrl(
    appendAttributionToParams(marketingAuthBrandParams(params)),
  );
}

export function appDashboardUrl(path: string = APP_DASHBOARD_PATH): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  // Support paths with query strings (e.g. /agents/hire/custom?new=1).
  return new URL(normalized, `${CLERK_AUTH_APP_ORIGIN}/`).href;
}

/**
 * App dashboard / hire URL, tagging SiteGlue marketing traffic with brand=siteglue.
 */
export function marketingAppDashboardUrl(path: string = APP_DASHBOARD_PATH): string {
  const href = appDashboardUrl(path);
  if (typeof window === "undefined") return href;
  const brand = getMarketingBrandFromHostname(window.location.hostname);
  return appendAttributionToUrl(withMarketingBrandParam(href, brand));
}

/** Avoid redirect loops when redirect_url points back to auth pages. */
export function sanitizeAuthRedirectPath(path: string): string {
  const pathname = path.split("?")[0] ?? path;
  if (pathname.startsWith(SIGN_IN_PATH) || pathname.startsWith(SIGN_UP_PATH)) {
    return APP_DASHBOARD_PATH;
  }
  return path;
}

export function buildSignUpHref(params?: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value) search.set(key, value);
  }
  const query = search.toString();
  return query ? `${SIGN_UP_PATH}?${query}` : SIGN_UP_PATH;
}

/** Origins allowed for Clerk OAuth redirects across marketing + app hosts. */
export const CLERK_AUTH_REDIRECT_ORIGINS = [
  "https://easygpt.builders",
  "https://www.easygpt.builders",
  "https://app.easygpt.builders",
  "https://staging.easygpt.builders",
  "https://siteglue.ai",
  "https://www.siteglue.ai",
  "https://app.practiceclaw.com",
  "https://www.practiceclaw.com",
] as const;

/** App origin for builder/dashboard — marketing hosts redirect here after hire. */
export function resolveEasyGptAppOrigin(): string {
  const fromEnv = process.env.NEXT_PUBLIC_APP_URL?.trim().replace(/\/$/, "");
  if (fromEnv) return fromEnv;

  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    if (
      isEasyGPTMarketingHost(hostname) ||
      isSiteGlueMarketingHost(hostname)
    ) {
      return CLERK_AUTH_APP_ORIGIN;
    }
    return window.location.origin;
  }

  return CLERK_AUTH_APP_ORIGIN;
}

export function buildProjectBuilderUrl(
  projectId: string,
  query?: { importWebsite?: string; onboarding?: string },
): string {
  const url = new URL(`/projects/${projectId}/builder`, resolveEasyGptAppOrigin());
  if (query?.importWebsite) {
    url.searchParams.set("importWebsite", query.importWebsite);
  }
  if (query?.onboarding) {
    url.searchParams.set("onboarding", query.onboarding);
  }
  return url.href;
}

/** Settings → My Profile (appearance) after hire / Stripe checkout. */
export function buildProjectDashboardProfileUrl(
  projectId: string,
  query?: { importWebsite?: string },
): string {
  const url = new URL(
    `/projects/${projectId}/settings`,
    resolveEasyGptAppOrigin(),
  );
  url.searchParams.set("tab", "appearance");
  if (query?.importWebsite?.trim()) {
    url.searchParams.set("importWebsite", query.importWebsite.trim());
  }
  return url.href;
}

/** Full navigation when target is on the app subdomain (e.g. hire from www). */
export function navigateToAppUrl(url: string): void {
  if (typeof window === "undefined") return;
  window.location.assign(url);
}

/** Query param for passing hero prompt across marketing → app navigation. */
export const POST_AI_JOB_PROMPT_QUERY_KEY = "prompt";

export function buildPostAiJobAbsoluteUrl(
  prompt: string,
  path = "/employers/post-ai-job",
): string {
  const url = new URL(path, resolveEasyGptAppOrigin());
  url.searchParams.set(POST_AI_JOB_PROMPT_QUERY_KEY, prompt);
  if (typeof window !== "undefined") {
    const brand = getMarketingBrandFromHostname(window.location.hostname);
    if (brand === "siteglue") {
      url.searchParams.set(MARKETING_BRAND_QUERY_KEY, MARKETING_BRAND_SITEGLUE);
    }
  }
  return url.href;
}
